// Sinutech Pagos brand palette. These are the only source of truth for color across the
// app — Vuestic components read them as CSS custom properties (--va-primary, etc.) and
// tailwind.config.js's color tokens proxy the same variables, so this file alone drives
// VaButton/VaCard/VaBadge/... AND any `bg-primary`/`text-ink`-style Tailwind class.
export default {
  presets: {
    light: {
      // Surfaces
      backgroundPrimary: '#F5F5F7', // app canvas — cool light grey, not beige
      backgroundSecondary: '#FFFFFF', // elevated surfaces: cards, modals
      backgroundElement: '#EEF0F3', // recessed chips/toggles/inactive badges on white cards
      backgroundBorder: '#E5E7EB', // 1px hairline borders (cards, table rows, dividers)
      backgroundCardPrimary: '#FFFFFF',
      backgroundCardSecondary: '#F5F5F7',

      // Text
      textPrimary: '#17171A', // "ink"
      textInverted: '#FFFFFF',

      // Brand — "Cosmic Orange" (#F77E2D, Apple's own orange).
      // IMPORTANT: white text on this only clears ~2.6:1 (fails WCAG AA's 4.5:1). Deliberately
      // NOT compensated with a hardcoded onPrimary here — Vuestic auto-picks each component's
      // own text color per-instance (see useColors' getTextColor: a perceived-brightness check
      // against a threshold of 150/255) and #F77E2D scores ~160.7, just over that threshold, so
      // components asking Vuestic for the contrasting color (VaButton, VaCard, VaBadge, ...)
      // already render dark ink text automatically — verified live (see PROGRESS.md-style note
      // in the task summary) rather than assumed. That auto-pick is why there's no onPrimary
      // token to set here, unlike sidebarActiveBackground below, which is consumed as a plain
      // hardcoded color (not through Vuestic's color props) and so needed its own manual check.
      primary: '#F77E2D',
      secondary: '#6B7280', // neutral muted text/icons (tables, secondary labels) — unaffected by hue, kept as-is
      // Warm gold/amber — distinct from `primary` (orange) so notification badges etc. don't
      // read as "just another orange". Ink text clears ~8.3:1; white only ~2.2:1 (fails AA).
      accent: '#E8A339',
      onAccent: '#17171A',
      focus: '#F77E2D', // mirrors `primary`

      // Status colors — success/danger/info keep their original hues (green/red/blue), none of
      // which sit anywhere near orange on the color wheel, so they still read unambiguously
      // against the new primary. `warning` is the one exception: the old amber (#D97706, hue
      // ~32°) sat only ~8° from the new primary's hue (~24°) — close enough that a warning
      // badge next to a primary button could blend together. Shifted to a clearly more yellow
      // gold (#CA8A04, hue ~41°) to keep it visually distinct from `primary` while still reading
      // as "warning" (ink text clears ~6.1:1).
      success: '#17A468',
      warning: '#CA8A04',
      danger: '#DC2626',
      info: '#3E63DD',

      shadow: 'rgba(23, 23, 26, 0.35)',

      // Sidebar — deliberately fixed (same values in both presets, not theme-reactive): the
      // "dark sidebar / light content" pattern is a permanent layout choice, not something that
      // should flip with the light/dark mode toggle (AppSidebar.vue reads these directly instead
      // of the surface/text tokens above).
      sidebarBackground: '#1B1B1F', // neutral near-black carbon — no violet tint to clash with orange
      sidebarText: '#ADA9A3', // warm-neutral light grey for inactive item icons/labels — ~7.3:1 on sidebarBackground
      sidebarBorder: 'rgba(255, 255, 255, 0.08)', // hairline for the floating item "cards" on a dark fill — hue-agnostic, unchanged
      // Solid fill for the active item. NOT the plain `primary` (#F77E2D) — this token is read
      // directly as a literal color by AppSidebar.vue/AppNavbar.vue rather than through
      // Vuestic's color props, so it doesn't get the auto dark/light text pick `primary` gets
      // elsewhere (see the comment above). Paired with a hardcoded white `sidebarActiveText`,
      // #F77E2D itself only clears ~2.6:1 — this darker/more saturated "burnt orange" variant
      // clears ~5.1:1, comfortably past the 4.5:1 AA minimum.
      sidebarActiveBackground: '#C1440E',
      sidebarActiveText: '#FFFFFF', // paired with sidebarActiveBackground — ~5.1:1 contrast
    },
    dark: {
      backgroundPrimary: '#0F0F13',
      backgroundSecondary: '#1B1B22',
      backgroundElement: '#26262F',
      backgroundBorder: '#33333D',
      backgroundCardPrimary: '#1B1B22',
      backgroundCardSecondary: '#16161B',

      textPrimary: '#F2F2F5',
      textInverted: '#17171A',

      // Lightened/more saturated orange for dark surfaces — same reasoning as the light preset's
      // primary: white text on it only clears ~2.2:1, but Vuestic's own auto text-color pick
      // (perceived brightness ~178 vs the 150 threshold) already selects dark ink automatically
      // wherever a color is read through Vuestic's props, which clears ~8:1.
      primary: '#FF9152',
      secondary: '#9CA3AF',
      // Brighter gold to match, same distinct-from-primary reasoning as the light preset.
      accent: '#F3B94D',
      onAccent: '#17171A', // ink on accent clears ~10.1:1
      focus: '#FF9152',

      success: '#34D399',
      // Hue (~43°) already sits well clear of the new dark-mode primary's hue (~22°) — no change
      // needed here, unlike the light preset's warning.
      warning: '#FBBF24',
      danger: '#F87171',
      info: '#7C93F0',

      shadow: 'rgba(0, 0, 0, 0.6)',

      // Same fixed sidebar palette as the light preset — see the comment there.
      sidebarBackground: '#1B1B1F',
      sidebarText: '#ADA9A3',
      sidebarBorder: 'rgba(255, 255, 255, 0.08)',
      sidebarActiveBackground: '#C1440E',
      sidebarActiveText: '#FFFFFF',
    },
  },
}
